import { SafetyCertificateOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { localizedSystemName } from '../lib/tenant';

type PublicBranding = {
  systemName: string | null;
  systemNameZh: string | null;
  systemNameEn: string | null;
  nameZh: string | null;
  nameEn: string | null;
  logoUrl: string | null;
  passwordMinLength: number;
};

export function usePublicBranding() {
  const { t, i18n } = useTranslation();
  const [branding, setBranding] = useState<PublicBranding | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetch('/api/v1/system/branding', {
      signal: controller.signal,
      cache: 'no-store',
    })
      .then(async (response) => {
        if (response.ok) setBranding((await response.json()) as PublicBranding);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const siteName = localizedSystemName(
    branding,
    i18n.resolvedLanguage,
    t('app.name'),
  );

  useEffect(() => {
    document.title = siteName;
  }, [siteName]);

  return {
    siteName,
    logoUrl: branding?.logoUrl ?? null,
    passwordMinLength: branding?.passwordMinLength ?? 12,
  };
}

export function AuthBrand({
  siteName,
  logoUrl,
}: {
  siteName: string;
  logoUrl: string | null;
}) {
  const { t } = useTranslation();
  return (
    <Link className="brand" to="/login" aria-label={siteName}>
      <span className={`brand-mark${logoUrl ? ' brand-mark--image' : ''}`} aria-hidden="true">
        {logoUrl ? <img src={logoUrl} alt="" /> : <SafetyCertificateOutlined />}
      </span>
      <span>
        <strong>{siteName}</strong>
        {siteName !== t('app.name') && <small>{t('app.name')}</small>}
      </span>
    </Link>
  );
}
